import React, { useState, useEffect } from "react";
import { FaArrowDown } from "react-icons/fa";

const Hero = () => {
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      setMousePosition({
        x: (e.clientX / window.innerWidth) * 100,
        y: (e.clientY / window.innerHeight) * 100,
      });
    };

    window.addEventListener("mousemove", handleMouseMove);
    return () => window.removeEventListener("mousemove", handleMouseMove);
  }, []);

  return (
    <section
      id="home"
      className="relative min-h-[85vh] flex items-center overflow-hidden bg-[#0a0a0f] text-white"
    >
      {/* Animated background elements */}
      <div className="absolute inset-0 overflow-hidden">
        {/* Gradient orbs */}
        <div
          className="absolute w-[600px] h-[600px] bg-blue-500/20 rounded-full blur-[120px] animate-float"
          style={{
            left: `${mousePosition.x * 0.5}%`,
            top: `${mousePosition.y * 0.5}%`,
            transform: "translate(-50%, -50%)",
            transition: "all 0.5s ease-out",
          }}
        ></div>
        <div
          className="absolute w-[500px] h-[500px] bg-purple-500/20 rounded-full blur-[100px] animate-float"
          style={{
            left: `${100 - mousePosition.x * 0.5}%`,
            top: `${100 - mousePosition.y * 0.5}%`,
            transform: "translate(-50%, -50%)",
            transition: "all 0.5s ease-out",
            animationDelay: "1s",
          }}
        ></div>

        {/* Grid pattern */}
        <div className="absolute inset-0 opacity-[0.03]">
          <div
            className="absolute inset-0"
            style={{
              backgroundImage: `linear-gradient(rgba(255,255,255,0.5) 1px, transparent 1px),
                               linear-gradient(90deg, rgba(255,255,255,0.5) 1px, transparent 1px)`,
              backgroundSize: "60px 60px",
            }}
          ></div>
        </div>
      </div>

      {/* Content */}
      <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 w-full pt-24 pb-20">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          {/* Left Column - Main Content */}
          <div className="lg:col-span-7 space-y-8">
            {/* Greeting */}
            <div data-aos="fade-up">
              <p className="text-lg text-gray-400 font-medium">
                Hi, my name is
              </p>
            </div>

            {/* Name */}
            <div data-aos="fade-up" data-aos-delay="100" className="space-y-2">
              <h1 className="text-5xl sm:text-6xl md:text-7xl lg:text-8xl font-bold leading-tight">
                <span className="text-white block">Roshan</span>
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 via-purple-400 to-pink-400 block">
                  Karki
                </span>
              </h1>
              <p className="text-2xl sm:text-3xl md:text-4xl text-gray-300 font-medium mt-4">
                Backend Engineer
              </p>
            </div>

            {/* Description */}
            <div
              data-aos="fade-up"
              data-aos-delay="200"
              className="max-w-2xl space-y-4 text-lg text-gray-400 leading-relaxed"
            >
              <p>
                Software engineer who loves building scalable, high-performance
                applications. Currently working at{" "}
                <a
                  href="https://www.roaspy.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-400 font-semibold hover:text-blue-300 underline transition-colors duration-300"
                >
                  Roaspy
                </a>{" "}
                as a Backend Engineer.
              </p>
              <p>
                With 3+ years of experience, I love building backend systems and
                being part of the programming culture. Passionate about database
                optimization, API development, and creating efficient solutions.
              </p>
            </div>

            {/* CTA Buttons */}
            <div
              data-aos="fade-up"
              data-aos-delay="300"
              className="flex flex-wrap gap-4 pt-4"
            >
              <a
                href="#projects"
                className="group px-8 py-4 bg-blue-500 text-white rounded-lg font-medium hover:bg-blue-600 transition-all duration-300 transform hover:scale-105 hover:shadow-lg hover:shadow-blue-500/50 flex items-center gap-2"
              >
                View My Work
                <span className="group-hover:translate-x-1 transition-transform inline-block">
                  →
                </span>
              </a>
              <a
                href="#contact"
                className="px-8 py-4 bg-white/10 border border-white/20 text-white rounded-lg font-medium hover:bg-white/20 hover:border-white/30 transition-all duration-300 backdrop-blur-sm"
              >
                Get In Touch
              </a>
            </div>
          </div>

          {/* Right Column - Visual Element or Stats */}
          <div className="lg:col-span-5 lg:pl-8">
            <div data-aos="fade-left" data-aos-delay="200" className="relative">
              {/* Decorative box */}
              <div className="relative bg-white/5 border border-white/10 rounded-2xl p-8 backdrop-blur-sm">
                <div className="absolute top-4 right-4 w-3 h-3 bg-green-500 rounded-full animate-pulse"></div>

                {/* Stats or highlights */}
                <div className="space-y-6 mt-4">
                  <div>
                    <div className="text-4xl font-bold text-white mb-1">3+</div>
                    <div className="text-sm text-gray-400">
                      Years Experience
                    </div>
                  </div>
                  <div className="border-t border-white/10 pt-6">
                    <div className="text-sm text-gray-400 mb-3">
                      Currently at
                    </div>
                    <div className="text-xl font-semibold text-blue-400">
                      Roaspy
                    </div>
                    <div className="text-sm text-gray-400 mt-1">
                      Backend Engineer
                    </div>
                  </div>
                  <div className="border-t border-white/10 pt-6">
                    <div className="text-sm text-gray-400 mb-3">
                      Focus Areas
                    </div>
                    <div className="flex flex-wrap gap-2">
                      <span className="px-3 py-1 bg-white/10 rounded text-xs text-gray-300">
                        Backend
                      </span>
                      <span className="px-3 py-1 bg-white/10 rounded text-xs text-gray-300">
                        APIs
                      </span>
                      <span className="px-3 py-1 bg-white/10 rounded text-xs text-gray-300">
                        Databases
                      </span>
                      <span className="px-3 py-1 bg-white/10 rounded text-xs text-gray-300">
                        Optimization
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 flex flex-col items-center gap-2 text-gray-500">
        <span className="text-xs">Scroll to explore</span>
        <FaArrowDown className="animate-bounce-slow text-sm" />
      </div>
    </section>
  );
};

export default Hero;
